/**
 * The sections the Follow-ups page renders — the source of truth for
 * rendering and validation. The parser (wire → model) lives separately in
 * `@/lib/utils/clinic-configuration/kora/kora-parsers.ts`; this file holds
 * only data description, no runtime logic.
 *
 * Page schema, not resource schema: follow-ups are stored on the same
 * `Basic` as the Kora Behavior sections, and `clinicConfigurationSchema`
 * is the whole of it. Anything resolving a section against what is stored
 * belongs there, not here.
 *
 * `required` mirrors `min` on the matching slice in the backend's
 * `fhir-toolkit/src/fhir_toolkit/fhir_extensions.py` StructureDefinitions —
 * see `kora-config.schema.ts` for why nothing on the server enforces it.
 */
import Clock from "lucide-react/dist/esm/icons/clock-2";

import type { ConfigSchema } from "@/types/clinic-configuration";
import {
  FHIR_EXTENSION_URLS,
  FHIR_EXTENSIONS,
} from "@/constants/fhir.constants";
import { ROUTES } from "@/constants/routes.constants";

/**
 * Needs for i18next-scanner
 */
// t('Follow-ups')
// t('Follow-up messages sent when a conversation goes quiet')
// t('Follow-up ID')
// t('Identifier for this follow-up')
// t('Delay')
// t('How long after the previous message this follow-up is sent')
// t('Guidelines')
// t('Instructions the agent writes this follow-up from')
// t('Send windows')
// t('Hours of the week this follow-up may be sent in')
export const followUpsSchema = {
  extensions: {
    followUps: {
      kind: "array",
      slug: FHIR_EXTENSIONS.KORA_CONFIG_FOLLOW_UP,
      title: "Follow-ups",
      description: "Follow-up messages sent when a conversation goes quiet",
      /**
       * The page is nothing but this list, so each follow-up gets its own
       * card headed by its ID — the value an ops person already uses to talk
       * about it.
       */
      presentation: {
        layout: "section",
      },
      fields: {
        id: {
          kind: "text",
          slug: FHIR_EXTENSION_URLS.KORA_CONFIG_FOLLOW_UP.ID,
          title: "Follow-up ID",
          description: "Identifier for this follow-up",
          readonly: true,
          orientation: "horizontal",
        },
        delay: {
          kind: "duration",
          slug: FHIR_EXTENSION_URLS.KORA_CONFIG_FOLLOW_UP.DELAY,
          title: "Delay",
          description:
            "How long after the previous message this follow-up is sent",
          required: true,
        },
        guidelines: {
          kind: "text",
          slug: FHIR_EXTENSION_URLS.KORA_CONFIG_FOLLOW_UP.GUIDELINES,
          title: "Guidelines",
          description: "Instructions the agent writes this follow-up from",
          layout: "stacked",
        },
        sendWindows: {
          kind: "weekSchedule",
          slug: FHIR_EXTENSION_URLS.KORA_CONFIG_FOLLOW_UP.SEND_WINDOW,
          title: "Send windows",
          description: "Hours of the week this follow-up may be sent in",
        },
      },
    },
  },
} as const satisfies ConfigSchema.Resource;

export const followUpsPage = {
  to: ROUTES.AGENT_FOLLOW_UPS,
  label: "Follow-ups",
  icon: Clock,
} satisfies ConfigSchema.Page;
